/**
 * AuditLogExpandedDetails component - shows detailed view of an audit log entry.
 *
 * This directory contains the split components:
 * - index.tsx: Main component (this file)
 * - field-helpers.ts: Field type detection helpers
 * - format-display-value.ts: Value formatting utilities
 * - value-renderers.tsx: Value rendering components
 */

import React, { useState, useMemo } from 'react';
import { Box, Text, Stack, Heading, CodeBlock, Modal, ModalInset, Link } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { AuditLogEntry } from '../../types';
import {
  formatAttributeName,
  formatPermissionValue,
  isRedactedValue,
  shouldUseMonospace,
  shouldUseCodeStyle,
  isEmailValue,
  isNullableDbField,
  extractNullableDbValue,
} from '../../utils/formatting';
import { isCampaignMappedField } from '../../utils/campaign-mapping';
import { isEmptyString } from '@/utils/string';
import { CAMPAIGN_OBJECT_TYPES } from '../../constants/shared';
import { buildEntityUrl, getEntityTypeLabel } from '../../utils/entity-urls';
import { isComplexValue, isPermissionString, isIpAddressField, isIpAddressValue } from './field-helpers';
import { formatDisplayValue } from './format-display-value';
import {
  renderValue,
  renderObjectProperties,
  renderComplexValue,
  tryRenderUserField,
  ATTRIBUTE_LABEL_MIN_WIDTH,
  type SimpleAttribute,
  type CodeModalState,
  type IpAddressFilterOptions,
  type UserInfo,
} from './value-renderers';

export interface RoleDefinition {
  id: number;
  name: string;
}

interface AuditLogExpandedDetailsProps {
  log: AuditLogEntry;
  context: 'account' | 'workspace';
  contextId: number;
  startTime?: string; // ISO date string for filtering links
  endTime?: string; // ISO date string for filtering links
  allUsers?: UserInfo[]; // User data for resolving user IDs to names
  roleDefinitions?: Record<number, RoleDefinition> | null; // Role definitions for resolving role IDs to names
  segments?: Array<{ id: number; name: string }>; // Segment data for resolving segment IDs to names in conditions
  workspaces?: Array<{ id: number; name: string }>; // Workspace data for resolving environment IDs to names
  campaigns?: Array<{ id: number; name: string }>; // Campaign data for resolving campaign IDs to names in message conditions
  objectTypes?: Array<{ id: number; name: string }>; // Object types for resolving type IDs to names in relationship conditions
  newsletters?: Array<{ id: number; name: string }>; // Newsletter data for resolving newsletter IDs to names in newsletter_recipients conditions
  messages?: Array<{ output_id: string; subject?: string; name?: string; type?: string; campaign_id?: number }>; // Message data for resolving output_id (e.g., "output_5") to display names
}

// Entity URL utilities are imported from shared module

/**
 * Extract campaign_id from a CampaignAction log entry.
 * campaign_id can be in log.data.data (nested structure) or in changed_to/changed_from.
 * @param log - The audit log entry
 * @returns The campaign ID if found, null otherwise
 */
function extractCampaignIdFromCampaignAction(log: AuditLogEntry): number | null {
  if (log.object_type !== 'CampaignAction') {
    return null;
  }

  // First, try log.data.data (nested structure from API response)
  const logData = log.data as Record<string, unknown> | undefined;
  if (logData != null) {
    const nestedData = logData.data as Record<string, unknown> | undefined;
    if (nestedData != null && typeof nestedData.campaign_id === 'number') {
      return nestedData.campaign_id;
    }
    // Also try direct access (in case structure is different)
    if (typeof logData.campaign_id === 'number') {
      return logData.campaign_id;
    }
  }

  // Fallback: try changed_to (for updates)
  if (log.changed_to != null && typeof log.changed_to === 'object') {
    const changedTo = log.changed_to as Record<string, unknown>;
    if (typeof changedTo.campaign_id === 'number') {
      return changedTo.campaign_id;
    }
  }

  // Fallback: try changed_from (for deletes or if changed_to is empty)
  if (log.changed_from != null && typeof log.changed_from === 'object') {
    const changedFrom = log.changed_from as Record<string, unknown>;
    if (typeof changedFrom.campaign_id === 'number') {
      return changedFrom.campaign_id;
    }
  }

  return null;
}

export const AuditLogExpandedDetails: React.FC<AuditLogExpandedDetailsProps> = ({
  log,
  context,
  contextId,
  startTime,
  endTime,
  allUsers,
  roleDefinitions,
  segments = [],
  workspaces = [],
  campaigns = [],
  objectTypes = [],
  newsletters = [],
  messages = [],
}) => {
  // Track which code block is open in modal
  const [codeModalData, setCodeModalData] = useState<CodeModalState>(null);

  const isUpdatedAction = log.action.toLowerCase() === 'updated';

  // V2 API provides pre-computed diff via changed_from and changed_to fields
  // Check if we have ACTUAL content (not just empty objects)
  const hasChangedTo =
    log.changed_to != null && typeof log.changed_to === 'object' && Object.keys(log.changed_to).length > 0;
  const hasChangedFrom =
    log.changed_from != null &&
    typeof log.changed_from === 'object' &&
    Object.keys(log.changed_from as object).length > 0;

  // Determine what data to display based on action type
  // Handles both objects and primitives (string, number, boolean)
  const displayData = useMemo(() => {
    if (isUpdatedAction) {
      // For updates, prefer changed_to (new values), fall back to changed_from
      // This handles cases where backend provides changed_from but empty changed_to
      if (hasChangedTo) {
        return log.changed_to as unknown;
      }
      if (hasChangedFrom) {
        return log.changed_from as unknown;
      }
      return null;
    }
    if (log.action.toLowerCase() === 'deleted') {
      // For deletes, show what was deleted (changed_from)
      return hasChangedFrom ? (log.changed_from as unknown) : null;
    }
    // For creates and other actions, show changed_to (which contains full data)
    // Can be object, array, or primitive (string, number, boolean)
    if (log.changed_to != null) {
      return log.changed_to as unknown;
    }
    return null;
  }, [log.action, log.changed_from, log.changed_to, hasChangedTo, hasChangedFrom, isUpdatedAction]);

  // Check if we have changes to display for "Updated" actions
  // V2 API: check if either changed_to or changed_from has any fields
  const hasChanges = useMemo(() => {
    if (!isUpdatedAction) return false;
    return hasChangedTo || hasChangedFrom;
  }, [isUpdatedAction, hasChangedTo, hasChangedFrom]);

  // Check if displayData is a primitive value (not an object/array)
  const isPrimitiveData = displayData != null && typeof displayData !== 'object';

  // Check if displayData is an array
  const isArrayData = Array.isArray(displayData);

  // Check if this is a system event (user_id: 0)
  const isSystemEvent = log.user_id === 0;

  // Extract form name mapping from segment data (linked_form_id -> name)
  // This is used to display form names instead of form IDs in form conditions
  const formNames = useMemo(() => {
    if (displayData == null || typeof displayData !== 'object' || Array.isArray(displayData)) {
      return undefined;
    }
    const linkedFormId = (displayData as Record<string, unknown>).linked_form_id as string | undefined;
    const name = (displayData as Record<string, unknown>).name as string | undefined;
    if (linkedFormId != null && name != null) {
      return { [linkedFormId]: name };
    }
    return undefined;
  }, [displayData]);

  // Sort attributes A-Z and format for display (only for plain objects)
  // Split into two columns for row-first ordering (A-Z reading left-to-right, then next row)
  // Complex values (arrays, nested objects) go to a separate list rendered below
  // Calculate max attribute name width for each column to ensure vertical alignment
  const calculateMaxNameWidth = (attributes: SimpleAttribute[]): string => {
    if (attributes.length === 0) return ATTRIBUTE_LABEL_MIN_WIDTH;
    // Find the longest attribute name and add some padding
    const maxLength = Math.max(...attributes.map((attribute) => attribute.name.length));
    // Convert character length to approximate pixel width (rough estimate: 8px per character for medium weight text)
    const estimatedWidth = Math.max(maxLength * 8 + 20, 120); // Minimum 120px, or calculated width + padding
    return `${estimatedWidth}px`;
  };

  const { leftColumn, rightColumn, complexAttributes, leftColumnNameWidth, rightColumnNameWidth } = useMemo(() => {
    if (displayData == null || typeof displayData !== 'object' || Array.isArray(displayData)) {
      return {
        leftColumn: [],
        rightColumn: [],
        complexAttributes: [],
        leftColumnNameWidth: ATTRIBUTE_LABEL_MIN_WIDTH,
        rightColumnNameWidth: ATTRIBUTE_LABEL_MIN_WIDTH,
      };
    }

    // Fields to exclude from display (already shown in table columns or redundant)
    // - created/created_at/updated/last_updated: timestamp fields already shown in table
    // - email_changed_at: timestamp field that changes with email updates
    // - account_id: always the current account, redundant since users only see their own data
    // - created_by/updated_by: redundant for system events (user_id: 0) since it's always 0
    // - env_id: redundant for ProfileMerge since it's already shown in the workspace context
    // - last_environment_id: redundant, environment should show as workspace
    const excludedFields = new Set([
      'created',
      'created_at',
      'createdat',
      'updated',
      'updated_at',
      'last_updated',
      'lastupdated',
      'updatedat',
      'email_changed_at',
      'emailchangedat',
      'account_id',
      'accountid',
      'last_environment_id',
      'lastenvironmentid',
      // Exclude created_by and updated_by for system events
      ...(isSystemEvent ? ['created_by', 'createdby', 'updated_by', 'updatedby'] : []),
      // Exclude redundant fields for ProfileMerge (env_id, additional_data with email/user_id/type)
      ...(log.object_type === 'ProfileMerge' ? ['env_id', 'envid', 'env', 'additional_data', 'additionaldata'] : []),
      // Exclude email for User events (already shown in Activity line)
      ...(log.object_type === 'User' ? ['email'] : []),
      // Exclude internal fields for ObjectDeletion (chunk, env/workspace, offset are internal implementation details)
      ...(log.object_type === 'ObjectDeletion' ? ['chunk', 'env', 'offset'] : []),
    ]);

    const simple: SimpleAttribute[] = [];
    const complex: Array<{
      key: string;
      name: string;
      rawValue: unknown;
    }> = [];

    for (const [key, value] of Object.entries(displayData).sort(([a], [b]) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    )) {
      // Skip excluded fields (e.g., "updated" - already shown in timestamp column)
      if (excludedFields.has(key.toLowerCase().replace(/[_-]/g, ''))) {
        continue;
      }

      const formattedName = formatAttributeName(key);

      if (isComplexValue(value)) {
        complex.push({ key, name: formattedName, rawValue: value });
      } else {
        // Use campaign-specific formatting for Campaign/CampaignAction objects
        const formattedValue = formatDisplayValue(value, key, log.object_type);
        const isRedacted = isRedactedValue(value);
        const isBoolean = typeof value === 'boolean';
        const isEmail = isEmailValue(value);
        // Extract actual value from nullable DB fields for IP address check
        const actualValue = isNullableDbField(value)
          ? extractNullableDbValue(value as { string?: string; int64?: number; valid: boolean })
          : value;
        // Don't show IP filter for empty/null values
        const isIpAddress =
          formattedValue !== '—' &&
          actualValue != null &&
          actualValue !== '' &&
          (isIpAddressField(key) || isIpAddressValue(actualValue));
        const isNullOrEmpty = value == null || value === '';
        // For campaign objects with mapped fields, don't use code style (they have semantic labels)
        const isCampaignMapped = CAMPAIGN_OBJECT_TYPES.has(log.object_type) && isCampaignMappedField(key);
        const useCodeStyle =
          !isNullOrEmpty &&
          !isBoolean &&
          !isEmail &&
          !isIpAddress &&
          !isCampaignMapped &&
          (shouldUseCodeStyle(formattedName) || shouldUseMonospace(value, key));

        simple.push({
          key,
          name: formattedName,
          value: formattedValue,
          rawValue: value,
          isRedacted,
          isBoolean,
          isEmail,
          isIpAddress,
          useCodeStyle,
        });
      }
    }

    // Split simple attributes into two columns with column-first ordering
    // Left column gets first half (A-Z), right column gets second half (A-Z)
    // Example: [A, B, C, D] → Left: [A, B], Right: [C, D]
    const midpoint = Math.ceil(simple.length / 2);
    const left = simple.slice(0, midpoint);
    const right = simple.slice(midpoint);

    // Calculate max name width for each column to ensure vertical alignment
    const leftColumnNameWidth = calculateMaxNameWidth(left);
    const rightColumnNameWidth = calculateMaxNameWidth(right);

    return {
      leftColumn: left,
      rightColumn: right,
      complexAttributes: complex,
      leftColumnNameWidth,
      rightColumnNameWidth,
    };
  }, [displayData, log.object_type, isSystemEvent]);

  const hasAttributes = leftColumn.length > 0 || rightColumn.length > 0;
  const hasComplexAttributes = complexAttributes.length > 0;

  // IP address filter options for renderValue
  const ipFilterOptions: IpAddressFilterOptions = useMemo(
    () => ({
      context,
      contextId,
      startTime,
      endTime,
    }),
    [context, contextId, startTime, endTime],
  );

  // Build entity URL for linkable entities (only in workspace context)
  const entityUrl = useMemo(() => {
    // For CampaignAction, extract campaign_id and link to the parent campaign
    if (log.object_type === 'CampaignAction' && context === 'workspace') {
      const campaignId = extractCampaignIdFromCampaignAction(log);
      if (campaignId != null && campaignId > 0) {
        return buildEntityUrl('Campaign', campaignId, contextId);
      }
      return null;
    }

    // For other linkable entities
    const isLinkable = ['Campaign', 'Newsletter', 'TransactionalMessage', 'Segment'].includes(log.object_type);
    if (isLinkable && context === 'workspace') {
      return buildEntityUrl(log.object_type, log.object_id, contextId);
    }
    return null;
  }, [log, context, contextId]);

  return (
    <Box p="300">
      <Stack gap="200">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading level="2" size="s">
            {isUpdatedAction && hasChanges ? 'Changed attributes' : 'Log entry'}
          </Heading>
          {entityUrl != null ? (
            <Link href={entityUrl} isExternal={true} icon="chevron-right" iconPosition="trailing">
              See {log.object_type === 'CampaignAction' ? 'campaign' : getEntityTypeLabel(log.object_type)}
            </Link>
          ) : null}
        </Box>
        {isPrimitiveData ? (
          // Display primitive values (string, number, boolean) directly
          <Box display="flex" gap="100">
            <Text size="s" weight="medium" flexShrink="0" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH }}>
              Value:
            </Text>
            <Text size="s" color="subtle" overflowWrap="anywhere">
              {formatDisplayValue(displayData, 'value', log.object_type)}
            </Text>
          </Box>
        ) : isArrayData ? (
          // Display top-level arrays - check if they contain objects
          (() => {
            const array = displayData as unknown[];
            // Check for plain objects (not nested arrays)
            const hasObjects =
              array.length > 0 &&
              array.every((item) => item != null && typeof item === 'object' && !Array.isArray(item));

            if (hasObjects) {
              // Use "Object" label for ObjectDeletion, "Item" for others
              const itemLabel = log.object_type === 'ObjectDeletion' ? 'Object' : 'Item';
              // For ObjectDeletion, exclude internal implementation fields
              const arrayItemExcludedFields =
                log.object_type === 'ObjectDeletion' ? new Set(['chunk', 'env', 'offset']) : new Set<string>();
              return (
                <Stack gap="200">
                  {array.map((item, index) => (
                    <Box
                      key={index}
                      p="200"
                      style={{
                        borderRadius: themeVars.radius['4'],
                        border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
                      }}
                    >
                      <Text size="xs" weight="medium" color="subtle" mb="100">
                        {itemLabel} {index + 1}
                      </Text>
                      <Stack gap="050">
                        {renderObjectProperties(
                          item as Record<string, unknown>,
                          log.object_type,
                          `${index}-`,
                          arrayItemExcludedFields,
                          allUsers,
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              );
            }

            // Check if this is a permission array
            // Note: Must check length > 0 because Array.every() returns true for empty arrays (vacuous truth)
            const isPermissionArray =
              array.length > 0 && array.every((v) => typeof v === 'string' && isPermissionString(v as string));

            if (isPermissionArray) {
              return (
                <Stack gap="050">
                  {array.map((item, index) => (
                    <Text key={index} size="s" color="subtle" overflowWrap="anywhere">
                      •{' '}
                      {typeof item === 'string' && isPermissionString(item)
                        ? formatPermissionValue(item)
                        : formatDisplayValue(item, 'item', log.object_type)}
                    </Text>
                  ))}
                </Stack>
              );
            }

            // Simple array - display as comma-separated values
            return (
              <Box display="flex" alignItems="baseline" gap="100">
                <Text size="s" weight="medium" flexShrink="0" style={{ minWidth: ATTRIBUTE_LABEL_MIN_WIDTH }}>
                  Values:
                </Text>
                <Text size="s" color="subtle" overflowWrap="anywhere">
                  {(() => {
                    const joined = array.map((v) => formatDisplayValue(v, 'value', log.object_type)).join(', ');
                    return !isEmptyString(joined) ? joined : '—';
                  })()}
                </Text>
              </Box>
            );
          })()
        ) : hasAttributes || hasComplexAttributes ? (
          <Stack gap="300">
            {/* Simple attributes in two columns */}
            {hasAttributes ? (
              <Box display="flex" gap="400">
                {/* Left column - first half of sorted attributes */}
                <Box flex={1}>
                  <Stack gap="100">
                    {leftColumn.map((attribute) => {
                      // Try special user field rendering (user_id in permissions, created_by/updated_by in Asset)
                      const userFieldElement = tryRenderUserField(attribute, log.object_type, allUsers);
                      if (userFieldElement != null) {
                        return userFieldElement;
                      }

                      return (
                        <Box key={attribute.key} display="flex" alignItems="baseline" gap="100" flexWrap="wrap">
                          <Text size="s" weight="medium" flexShrink="0" style={{ width: leftColumnNameWidth }}>
                            {attribute.name}:
                          </Text>
                          <Box flex={1} style={{ minWidth: 0 }}>
                            {renderValue(attribute, ipFilterOptions)}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
                {/* Right column - second half of sorted attributes */}
                {rightColumn.length > 0 ? (
                  <Box flex={1}>
                    <Stack gap="100">
                      {rightColumn.map((attribute) => {
                        // Try special user field rendering (user_id in permissions, created_by/updated_by in Asset)
                        const userFieldElement = tryRenderUserField(attribute, log.object_type, allUsers);
                        if (userFieldElement != null) {
                          return userFieldElement;
                        }

                        return (
                          <Box key={attribute.key} display="flex" alignItems="baseline" gap="100" flexWrap="wrap">
                            <Text size="s" weight="medium" flexShrink="0" style={{ width: rightColumnNameWidth }}>
                              {attribute.name}:
                            </Text>
                            <Box flex={1} style={{ minWidth: 0 }}>
                              {renderValue(attribute, ipFilterOptions)}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ) : null}
              </Box>
            ) : null}

            {/* Complex attributes (arrays, nested objects, code strings) rendered below */}
            {hasComplexAttributes
              ? complexAttributes.map((attribute) =>
                  renderComplexValue({
                    value: attribute.rawValue,
                    name: attribute.name,
                    key: attribute.key,
                    objectType: log.object_type,
                    onOpenCodeModal: setCodeModalData,
                    context,
                    contextId,
                    startTime,
                    endTime,
                    allUsers,
                    roleDefinitions,
                    segments,
                    workspaces,
                    relatedLogs: log.related_logs,
                    campaigns,
                    formNames,
                    objectTypes,
                    newsletters,
                    messages,
                  }),
                )
              : null}
          </Stack>
        ) : (
          <Text size="s" color="subtle">
            No data available.
          </Text>
        )}
      </Stack>

      {/* Code content modal */}
      {codeModalData != null ? (
        <Modal isOpen={true} onClose={() => setCodeModalData(null)} title={codeModalData.name} size="md">
          <ModalInset overflow="hidden">
            <CodeBlock
              code={codeModalData.code}
              language={codeModalData.language as 'html' | 'json' | 'plain'}
              mode="dark"
              darkTheme="night-owl"
              withCopyButton={true}
              withLineNumbers={true}
              isRounded={false}
              style={{
                minHeight: '200px',
                maxHeight: '600px',
              }}
            />
          </ModalInset>
        </Modal>
      ) : null}
    </Box>
  );
};

// Re-export utilities for external use
export { formatDisplayValue } from './format-display-value';
export {
  isPermissionField,
  isPermissionString,
  isComplexValue,
  isIpAddressField,
  isIpAddressValue,
} from './field-helpers';
export {
  renderValue,
  renderObjectProperties,
  renderComplexValue,
  renderFilterTextWithLinks,
  ATTRIBUTE_LABEL_MIN_WIDTH,
  NESTED_OBJECT_MIN_WIDTH,
} from './value-renderers';
export type { SimpleAttribute, CodeModalState, UserInfo } from './value-renderers';
